/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import GridTable from "../../../components/GridTable/GridTable";
import { DynamicFilterConfig } from "../../../components/DynamicComponent/DynamicFilterPanel";
import endpoints from "../../../services/endpoints";
import { setListOfUsers } from "../../../store/slices/selectedBankIdSlice";
import { convertTimestamp } from "../../../components/commonfunctions/convertDate";

export default function ListOfCVMServiceFee() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { cvmServiceFee } = endpoints;
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>("transaction id");
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

  const TableDetails: { [key: string]: any } = {
    "Transaction ID": {
      id: "transaction_id",
    },
    "Date and Time": {
      id: "created_at",
      custom: ["created_at"],
      className: "min-w-[180px]",
    },
    Sender: {
      id: "sender",
    },
    Receiver: {
      id: "receiver",
    },
    "Recipient Location": {
      id: "recipient_location",
      className: "min-w-[200px]",
    },
    Amount: {
      id: "amount",
      custom: ["amount"],
    },
    Action: {
      id: "actions",
      actionTypes: ["view"],
      className: "w-[100px]",
    },
  };

  const handleActions = async (id: string, key: string, text?: any) => {
    dispatch(setListOfUsers(location.pathname + location.search));
    navigate(`/zoodpay-money/cvm-service-fee/${id}`);
  };

  const staticText = {
    emptyScreenText: {
      heading1: "No Data",
      heading2: "No records found.",
    },
  };

  const dynamicFilterConfig: DynamicFilterConfig = {
    "Date Range": {
      label: "Date Range",
      type: "date",
      required: false,
      searchKey: {
        start: "start_date",
        end: "end_date",
      },
    },
  };

  const SEARCH_PLACEHOLDERS = ["transaction id", "user code"] as const;

  const rotatePlaceholder = useCallback(() => {
    setIsTransitioning(true);

    setTimeout(() => {
      setSearchPlaceholder((prev) => {
        const currentIndex = SEARCH_PLACEHOLDERS.indexOf(
          prev as (typeof SEARCH_PLACEHOLDERS)[number]
        );
        const nextIndex = (currentIndex + 1) % SEARCH_PLACEHOLDERS.length;
        return SEARCH_PLACEHOLDERS[nextIndex];
      });
      setIsTransitioning(false);
    }, 500);
  }, []);

  useEffect(() => {
    const interval = setInterval(rotatePlaceholder, 2500);
    return () => clearInterval(interval);
  }, [rotatePlaceholder]);

  const renderExtraHeading = ({ entireResponse, loading }: any) => {
    if (loading) {
      return (
        <div className="flex justify-end px-6 pt-4">
          <div className="flex flex-col items-end gap-1">
            <div className="h-8 w-32 animate-pulse bg-gray-200 rounded"></div>
            <div className="h-4 w-24 animate-pulse bg-gray-200 rounded"></div>
          </div>
        </div>
      );
    }

    const totalDisbursed = entireResponse?.total_cvm_disbursed;
    const lastUpdated = entireResponse?.last_updated_at;

    return (
      <div className="flex justify-between items-start px-6 pt-4" data-testid="cvm-service-fee-header">
        <div className="flex flex-col">
          <h2 className="text-[24px] font-semibold text-primary_text" data-testid="cvm-list-title">
            List of All CVM Service Fee
          </h2>
          {lastUpdated && (
            <p className="text-[18px] font-[400] text-gray-500" data-testid="cvm-last-updated">
              Last updated on{" "}
              <span className="text-[18px] font-semibold text-p_primary">
                {convertTimestamp(lastUpdated)}
              </span>
            </p>
          )}
        </div>
        <div className="flex flex-col items-end">
          {totalDisbursed !== undefined && totalDisbursed !== null && (
            <>
              <span className="text-[24px] font-semibold text-p_primary" data-testid="cvm-total-disbursed">
                AFA {Number(totalDisbursed || 0).toFixed(2)}
              </span>
              <span className="text-[16px] font-[500] text-gray-500">Total CVM Disbursed</span>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Breadcrumbs
        navPath={["Zoodpay Money", "CVM Service Fee"]}
        heading="CVM Service Fee"
        noNavigation={true}
        list={true}
      />
      <GridTable
        staticText={staticText}
        mainEndPoint={cvmServiceFee}
        TableDetails={TableDetails}
        handleActions={(id: string, key: string, text?: string) =>
          handleActions(id, key, text)
        }
        params={{ page_number: "1", client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }}
        searchPlaceholder={`Search by ${searchPlaceholder}`}
        placeholderClassName={`search-placeholder ${isTransitioning ? "transitioning" : ""}`}
        filters={true}
        exporting={true}
        reportType="CVM Service Fee"
        notfoundRoute={{ state: { route: `/zoodpay-money/cvm-service-fee` } }}
        title="CVM Service Fee"
        hasViewMDPrivilege={true}
        addMorePadding={false}
        filterShimmer={true}
        dynamicFilterConfig={dynamicFilterConfig}
        customHeight="h-[103px]"
        forceFullWidth={true}
        renderExtraHeading={renderExtraHeading}
        mainDivScroll={true}
      />
    </>
  );
}

